import { useRef } from "react";
import { ProgressCircle, toast } from "@heroui/react";
import { ImagePlus, X } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import type { EvidenceSlot } from "./types";

const DEFAULT_MAX = 9;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

export type EvidenceImagePickerProps = {
  slots: EvidenceSlot[];
  onSlotsChange: (slots: EvidenceSlot[]) => void;
  uploadFile: (file: File, onProgress: (percent: number) => void) => Promise<string>;
  maxCount?: number;
  disabled?: boolean;
};

export function EvidenceImagePicker({
  slots,
  onSlotsChange,
  uploadFile,
  maxCount = DEFAULT_MAX,
  disabled = false,
}: EvidenceImagePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const slotsRef = useRef(slots);
  slotsRef.current = slots;

  const updateSlots = (updater: (prev: EvidenceSlot[]) => EvidenceSlot[]) => {
    const next = updater(slotsRef.current);
    slotsRef.current = next;
    onSlotsChange(next);
  };

  const runUpload = (slotId: string, file: File) => {
    uploadFile(file, (progress) => {
      updateSlots((prev) =>
        prev.map((s) =>
          s.kind === "pending" && s.id === slotId ? { ...s, progress } : s,
        ),
      );
    })
      .then((fileKey) => {
        updateSlots((prev) =>
          prev.map((s) =>
            s.kind === "pending" && s.id === slotId
              ? { ...s, status: "done" as const, progress: 100, fileKey }
              : s,
          ),
        );
      })
      .catch((error: Error) => {
        updateSlots((prev) =>
          prev.map((s) =>
            s.kind === "pending" && s.id === slotId
              ? {
                  ...s,
                  status: "error" as const,
                  error: error.message || "上传失败",
                }
              : s,
          ),
        );
      });
  };

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    updateSlots((prev) => {
      const remaining = maxCount - prev.length;
      if (remaining <= 0) {
        toast.danger(`最多 ${maxCount} 张图片`);
        return prev;
      }
      const slice = Array.from(files).slice(0, remaining);
      if (files.length > remaining) {
        toast.danger(`最多 ${maxCount} 张，已选取前 ${remaining} 张`);
      }
      const newItems: EvidenceSlot[] = [];
      for (const file of slice) {
        if (!ALLOWED.includes(file.type)) {
          toast.danger(`${file.name} 仅支持 JPG、PNG、WEBP`);
          continue;
        }
        if (file.size > MAX_BYTES) {
          toast.danger(`${file.name} 不能超过 10MB`);
          continue;
        }
        newItems.push({
          kind: "pending",
          id: uuidv4(),
          name: file.name,
          file,
          preview: URL.createObjectURL(file),
          status: "uploading",
          progress: 0,
        });
      }
      if (newItems.length === 0) return prev;
      queueMicrotask(() => {
        newItems.forEach((slot) => {
          if (slot.kind === "pending") runUpload(slot.id, slot.file);
        });
      });
      return [...prev, ...newItems];
    });
    e.target.value = "";
  };

  const removeSlot = (id: string) => {
    updateSlots((prev) => {
      const s = prev.find((x) => x.id === id);
      if (s?.kind === "pending") URL.revokeObjectURL(s.preview);
      return prev.filter((x) => x.id !== id);
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        multiple
        className="hidden"
        disabled={disabled}
        onChange={handlePick}
      />
      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
        {slots.map((slot) => (
          <div
            key={slot.id}
            className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
          >
            <img
              src={slot.kind === "existing" ? slot.url : slot.preview}
              alt={slot.kind === "pending" ? slot.name : ""}
              className="w-full h-full object-cover"
            />
            {slot.kind === "pending" && slot.status === "uploading" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
                <ProgressCircle aria-label="上传进度" size="sm" value={slot.progress}>
                  <ProgressCircle.Track>
                    <ProgressCircle.TrackCircle />
                    <ProgressCircle.FillCircle />
                  </ProgressCircle.Track>
                </ProgressCircle>
                <span className="text-white text-xs mt-1">{slot.progress}%</span>
              </div>
            )}
            {slot.kind === "pending" && slot.status === "error" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/55 p-1">
                <span className="text-white text-[10px] text-center leading-tight">
                  {slot.error || "上传失败"}
                </span>
              </div>
            )}
            {!disabled && (
              <button
                type="button"
                className="absolute top-1 right-1 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
                onClick={() => removeSlot(slot.id)}
                aria-label="移除图片"
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        {!disabled && slots.length < maxCount && (
          <button
            type="button"
            className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 text-gray-500 hover:border-primary hover:text-primary transition-colors"
            onClick={() => inputRef.current?.click()}
          >
            <ImagePlus size={22} />
            <span className="text-xs">添加图片</span>
          </button>
        )}
      </div>
    </div>
  );
}
